import type {
  CardCombination,
  CardInteraction,
  DrawnCard,
  ReadingMode,
  Spread,
  TriadCombination,
} from '@lilith-arcana-os/domain';

export type CombinationScope =
  | 'adjacent-pair'
  | 'nonadjacent-pair'
  | 'triad'
  | 'cluster'
  | 'spread-global';

export type CombinationPolarity =
  | 'reinforcement'
  | 'tension'
  | 'transformation'
  | 'bridge'
  | 'shadow'
  | 'neutral';

export type CombinationEvidenceSource =
  | 'feature'
  | 'rule'
  | 'curated'
  | 'association'
  | 'spread-context';

export interface CombinationFeature {
  id: string;
  label: string;
  scope: CombinationScope;
  weight: number;
  polarity: CombinationPolarity;
  value: unknown;
  source: CombinationEvidenceSource;
  tags?: string[];
}

export interface PairFeatureSet {
  pair: [number, number];
  features: CombinationFeature[];
}

export interface TriadFeatureSet {
  triad: [number, number, number];
  features: CombinationFeature[];
}

export interface ClusterFeatureSet {
  cardIndexes: number[];
  features: CombinationFeature[];
}

export interface CombinationTraceStep {
  id: string;
  scope: CombinationScope;
  subject: number[];
  ruleId?: string;
  curatedId?: string;
  matchedFeatureIds: string[];
  scoreDelta: number;
  summary: string;
}

export type CombinationRuleScope = 'pair' | 'triad' | 'cluster';

export interface CombinationRuleDefinition {
  id: string;
  scope: CombinationRuleScope;
  weight: number;
  tags: string[];
}

export interface PairCombinationResult {
  cards: [DrawnCard, DrawnCard];
  interaction: CardInteraction;
  score: number;
  confidence: number;
  trace: CombinationTraceStep[];
  tags: string[];
}

export interface TriadCombinationResult {
  cards: [DrawnCard, DrawnCard, DrawnCard];
  interaction: {
    type: string;
    description: string;
    pattern: 'progressive' | 'regressive' | 'cyclical' | 'conflictual' | 'harmonic';
    intensity: 1 | 2 | 3 | 4 | 5;
  };
  score: number;
  confidence: number;
  trace: CombinationTraceStep[];
  tags: string[];
}

export interface ClusterCombinationResult {
  cardIndexes: number[];
  label: string;
  description: string;
  score: number;
  confidence: number;
  tags: string[];
  trace: CombinationTraceStep[];
}

export interface CombinationEngineResult {
  pairs: PairCombinationResult[];
  triads: TriadCombinationResult[];
  clusters: ClusterCombinationResult[];
  dominantPattern: string | null;
  spreadGraphSummary: string | null;
}

export interface PairRuleModifier {
  id: string;
  pair: [number, number];
  scoreDelta: number;
  polarity?: CombinationPolarity;
  tags?: string[];
  summary: string;
}

export interface TriadRuleModifier {
  id: string;
  triad: [number, number, number];
  scoreDelta: number;
  pattern?: TriadCombinationResult['interaction']['pattern'];
  polarity?: CombinationPolarity;
  tags?: string[];
  summary: string;
}

export interface ClusterRuleModifier {
  id: string;
  cardIndexes: number[];
  scoreDelta: number;
  tags?: string[];
  summary: string;
}

export interface CuratedPairHit {
  curatedId: string;
  label: string;
  description: string;
  tags: string[];
  polarity: CombinationPolarity;
  intensity: 1 | 2 | 3 | 4 | 5;
  bonus: number;
}

export interface CuratedTriadHit {
  curatedId: string;
  label: string;
  description: string;
  tags: string[];
  pattern: TriadCombinationResult['interaction']['pattern'];
  intensity: 1 | 2 | 3 | 4 | 5;
  bonus: number;
}

export interface CombinationGraphSummary {
  graph: import('./graph/graph-model').CombinationGraph;
  density: number;
  hubIndex: number | null;
}

export interface AssembledCombinationNarratives {
  pairNotes: CardCombination[];
  triadNotes: TriadCombination[];
  dominantPattern: string | null;
  spreadGraphSummary: string | null;
}

export interface CombinationAnalysisInput {
  cards: DrawnCard[];
  spread: Spread;
  mode: ReadingMode;
}

export const clampScore = (value: number, max = 100): number =>
  Math.max(0, Math.min(max, Number(value.toFixed(3))));

export const clampConfidence = (value: number): number =>
  Math.max(0, Math.min(1, Number(value.toFixed(4))));

export const toInteractionIntensity = (score: number): 1 | 2 | 3 | 4 | 5 => {
  if (score >= 85) {
    return 5;
  }
  if (score >= 70) {
    return 4;
  }
  if (score >= 55) {
    return 3;
  }
  if (score >= 40) {
    return 2;
  }
  return 1;
};

export const normalizedPairKey = (left: string, right: string): string =>
  [left, right].sort().join('::');

export const normalizedTriadKey = (slugs: readonly string[]): string =>
  [...slugs].sort().join('::');

export const uniqueTags = (values: readonly string[]): string[] => Array.from(new Set(values));
